"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type PilatesDisplayPreviewProps = {
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * A bright reformer studio: daylight pours through a tall window on the left,
 * raking across an oak-slat wall and a light floor. A reformer — carriage,
 * springs, footbar, straps — is the focal point across the middle; a fiddle-
 * leaf fig holds the left third, a magic circle and a stability ball the right.
 *
 * One panoramic SVG (640×220, `slice`): a 301px feed card shows the composed
 * centre, a full-width post reveals more of the studio. The bottom-centre
 * participant pill sits on open floor in front of the reformer. Motion is one
 * compositor-only CSS loop (the daylight breathing), off under reduced motion.
 */
const PILATES_KEYFRAMES = `
@keyframes pil-light { 0%, 100% { opacity: 0.8 } 50% { opacity: 1 } }
.pil-light { animation: pil-light 7s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .pil-light { animation: none } }
`;

const FLOOR_TOP = 132;
const VP = { x: 320, y: 30 };
const seamX = (atTop: number, y: number) =>
  VP.x + (atTop - VP.x) * ((y - VP.y) / (FLOOR_TOP - VP.y));

const SLATS = Array.from({ length: 30 }, (_, i) => 246 + i * 13);

export function PilatesDisplayPreview({
  participants,
}: PilatesDisplayPreviewProps) {
  const uid = useId().replace(/[^\w-]/g, "");
  const id = (name: string) => `pil-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  return (
    <ActivityScenePreview
      label="Pilates"
      backgroundClassName="bg-[#efe2d2]"
      participants={participants}
      scene={
        <>
          <style href="pilates-scene-keyframes" precedence="default">
            {PILATES_KEYFRAMES}
          </style>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 640 220"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id={id("wall")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#f7eee3" />
                <stop offset="0.6" stopColor="#ecdccb" />
                <stop offset="1" stopColor="#d9c3ac" />
              </linearGradient>
              <linearGradient id={id("slat")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#c99c6c" />
                <stop offset="0.5" stopColor="#ddb487" />
                <stop offset="1" stopColor="#b9885a" />
              </linearGradient>
              <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#bfe0f6" />
                <stop offset="1" stopColor="#fdf7ea" />
              </linearGradient>
              <linearGradient id={id("floor")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#e6c9a2" />
                <stop offset="1" stopColor="#b88c5e" />
              </linearGradient>
              <linearGradient id={id("light")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#fffaf0" stopOpacity="0.7" />
                <stop offset="1" stopColor="#fffaf0" stopOpacity="0" />
              </linearGradient>
              <linearGradient id={id("rail")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#e9c595" />
                <stop offset="1" stopColor="#a8784a" />
              </linearGradient>
              <linearGradient id={id("pad")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#f6c6bc" />
                <stop offset="1" stopColor="#d48a80" />
              </linearGradient>
              <linearGradient id={id("chrome")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset="0.5" stopColor="#a9adb6" />
                <stop offset="1" stopColor="#5f6470" />
              </linearGradient>
              <radialGradient id={id("ball")} cx="0.35" cy="0.3" r="0.8">
                <stop offset="0" stopColor="#fff1ec" />
                <stop offset="0.35" stopColor="#f2aaa0" />
                <stop offset="1" stopColor="#b9606a" />
              </radialGradient>
              <linearGradient id={id("pot")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#4a4a4e" />
                <stop offset="0.6" stopColor="#2a2a2e" />
                <stop offset="1" stopColor="#141416" />
              </linearGradient>
            </defs>

            {/* Plaster wall + oak-slat feature wall */}
            <rect width="640" height={FLOOR_TOP} fill={url("wall")} />
            {SLATS.map((x) => (
              <rect
                key={x}
                x={x}
                y="0"
                width="10"
                height={FLOOR_TOP}
                fill={url("slat")}
              />
            ))}
            {SLATS.map((x) => (
              <rect
                key={x}
                x={x + 10}
                y="0"
                width="3"
                height={FLOOR_TOP}
                fill="#5a3a1e"
                opacity="0.55"
              />
            ))}

            {/* Tall window, left */}
            <rect
              x="148"
              y="14"
              width="80"
              height="118"
              rx="2"
              fill="#fbf6ee"
            />
            <rect x="153" y="19" width="70" height="110" fill={url("sky")} />
            <path
              d="M153 96 Q180 86 206 92 T223 90 L223 129 L153 129 Z"
              fill="#b9d6a8"
              opacity="0.7"
            />
            <rect x="187" y="19" width="2.4" height="110" fill="#fbf6ee" />
            <rect x="153" y="72" width="70" height="2.4" fill="#fbf6ee" />
            {/* Sheer curtain */}
            <path
              d="M144 10 L162 10 Q158 70 166 132 L144 132 Z"
              fill="#fffdf8"
              opacity="0.85"
            />
            <path
              d="M150 12 Q147 70 152 130 M156 12 Q154 70 160 130"
              stroke="#e8dccb"
              strokeWidth="0.8"
              fill="none"
            />

            {/* Daylight raking across the room */}
            <g className="pil-light">
              <polygon
                points="223,19 223,129 520,220 330,220"
                fill={url("light")}
                opacity="0.55"
              />
              <polygon
                points="246,40 246,128 420,128 300,40"
                fill="#fffaf0"
                opacity="0.18"
              />
            </g>

            {/* Floor */}
            <rect
              y={FLOOR_TOP}
              width="640"
              height={220 - FLOOR_TOP}
              fill={url("floor")}
            />
            {[-200, -80, 30, 130, 225, 320, 415, 510, 610, 720, 840].map(
              (x) => (
                <line
                  key={x}
                  x1={x}
                  y1={FLOOR_TOP}
                  x2={seamX(x, 220)}
                  y2="220"
                  stroke="#7a5230"
                  strokeOpacity="0.28"
                  strokeWidth="0.8"
                />
              ),
            )}
            <rect y={FLOOR_TOP - 3} width="640" height="4" fill="#f7eee3" />
            <rect
              y={FLOOR_TOP + 1}
              width="640"
              height="1.6"
              fill="#6b4626"
              opacity="0.3"
            />
            <polygon
              points="223,132 223,132 560,220 380,220"
              fill="#fffaf0"
              opacity="0.18"
            />

            {/* Pendant globe, right (clear of the label) */}
            <line
              x1="446"
              y1="0"
              x2="446"
              y2="34"
              stroke="#3a3430"
              strokeWidth="1"
            />
            <circle cx="446" cy="44" r="13" fill="#fff6dc" opacity="0.35" />
            <circle cx="446" cy="42" r="8.4" fill="#fff8e6" />
            <circle cx="443.4" cy="39.4" r="2.6" fill="#fff" />
            <rect x="443" y="32" width="6" height="3" rx="1" fill="#3a3430" />

            {/* The reformer */}
            <g>
              <ellipse
                cx="322"
                cy="158"
                rx="118"
                ry="6"
                fill="#6b4626"
                opacity="0.28"
              />
              {/* Frame rails + legs */}
              <rect
                x="214"
                y="136"
                width="216"
                height="12"
                rx="3"
                fill={url("rail")}
              />
              <rect
                x="214"
                y="136"
                width="216"
                height="1.6"
                rx="0.8"
                fill="#fff1d8"
              />
              <rect
                x="218"
                y="148"
                width="8"
                height="8"
                rx="1.5"
                fill="#7a5230"
              />
              <rect
                x="418"
                y="148"
                width="8"
                height="8"
                rx="1.5"
                fill="#7a5230"
              />
              {/* Springs from the footbar end to the carriage */}
              {[0, 1, 2].map((i) => (
                <g key={i}>
                  <path
                    d={`M240 ${139 + i * 2.6} ${Array.from({ length: 12 }, (_, k) => `L${244 + k * 4} ${139 + i * 2.6 + (k % 2 ? -1 : 1)}`).join(" ")}`}
                    fill="none"
                    stroke="#c9ccd4"
                    strokeWidth="0.8"
                  />
                  <circle
                    cx="240"
                    cy={139 + i * 2.6}
                    r="1.4"
                    fill={["#e0484a", "#f2b23a", "#3a8ae0"][i]}
                  />
                </g>
              ))}
              {/* Carriage */}
              <rect
                x="290"
                y="124"
                width="78"
                height="13"
                rx="3.5"
                fill={url("pad")}
              />
              <rect
                x="292"
                y="124.6"
                width="74"
                height="2.4"
                rx="1.2"
                fill="#ffe4dc"
              />
              {/* Shoulder blocks + headrest */}
              <rect
                x="356"
                y="110"
                width="6"
                height="15"
                rx="2.4"
                fill={url("pad")}
              />
              <rect
                x="364"
                y="112"
                width="6"
                height="13"
                rx="2.4"
                fill="#d48a80"
              />
              <rect
                x="370"
                y="121"
                width="12"
                height="5"
                rx="2"
                fill={url("pad")}
              />
              {/* Footbar */}
              <path
                d="M228 137 L234 106"
                stroke={url("chrome")}
                strokeWidth="3"
                strokeLinecap="round"
              />
              <rect
                x="226"
                y="101"
                width="20"
                height="7"
                rx="3.5"
                fill="#3a3430"
              />
              <rect
                x="228"
                y="102"
                width="16"
                height="1.6"
                rx="0.8"
                fill="#8a8078"
              />
              {/* Risers + pulleys + straps at the far end */}
              <path
                d="M424 137 L424 100"
                stroke={url("chrome")}
                strokeWidth="3"
                strokeLinecap="round"
              />
              <circle cx="424" cy="100" r="3.6" fill="#3a3430" />
              <circle cx="424" cy="100" r="1.4" fill="#c9ccd4" />
              <path
                d="M421 101 Q396 112 372 128"
                stroke="#2c2a28"
                strokeWidth="1.2"
                fill="none"
              />
              <path
                d="M370 126 q-6 4 -2 9 q4 3 6 -3 Z"
                fill="none"
                stroke="#2c2a28"
                strokeWidth="1.6"
              />
            </g>

            {/* Fiddle-leaf fig, left third */}
            <g>
              <ellipse
                cx="200"
                cy="166"
                rx="22"
                ry="3.4"
                fill="#6b4626"
                opacity="0.35"
              />
              <path
                d="M186 140 L214 140 L211 166 L189 166 Z"
                fill={url("pot")}
              />
              <rect
                x="185"
                y="138"
                width="30"
                height="3"
                rx="1.5"
                fill="#55555a"
              />
              <path
                d="M200 140 Q198 110 201 70"
                stroke="#5a3a1e"
                strokeWidth="2"
                fill="none"
              />
              {[
                { x: 196, y: 120, a: -40 },
                { x: 205, y: 112, a: 38 },
                { x: 194, y: 100, a: -30 },
                { x: 206, y: 92, a: 32 },
                { x: 197, y: 82, a: -20 },
                { x: 204, y: 74, a: 18 },
                { x: 201, y: 66, a: 0 },
              ].map((leaf, i) => (
                <g
                  key={`${leaf.x}-${leaf.y}`}
                  transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.a})`}
                >
                  <path
                    d="M0 0 C-8 -2 -9 -14 -4 -18 C-1 -20 1 -20 4 -18 C9 -14 8 -2 0 0 Z"
                    fill={i % 2 ? "#3f7a3a" : "#2f6a32"}
                  />
                  <path
                    d="M0 0 L0 -17"
                    stroke="#a9d68a"
                    strokeWidth="0.6"
                    opacity="0.7"
                  />
                  <path
                    d="M3 -16 C7 -12 7 -6 4 -3"
                    fill="none"
                    stroke="#c4f0a0"
                    strokeWidth="0.8"
                    opacity="0.6"
                  />
                </g>
              ))}
            </g>

            {/* Magic circle leaning on the wall + stability ball, right third */}
            <g transform="translate(-12 0)">
              <ellipse
                cx="458"
                cy="160"
                rx="16"
                ry="2.6"
                fill="#6b4626"
                opacity="0.3"
              />
              <ellipse
                cx="458"
                cy="138"
                rx="15"
                ry="20"
                fill="none"
                stroke="#7aa88a"
                strokeWidth="4"
                transform="rotate(-10 458 138)"
              />
              <ellipse
                cx="458"
                cy="138"
                rx="15"
                ry="20"
                fill="none"
                stroke="#c9ecd2"
                strokeWidth="0.9"
                strokeDasharray="10 40"
                transform="rotate(-10 458 138)"
              />
              <rect
                x="450"
                y="114"
                width="12"
                height="7"
                rx="3"
                fill="#3f6a4c"
                transform="rotate(-10 456 117)"
              />
              <rect
                x="455"
                y="155"
                width="12"
                height="7"
                rx="3"
                fill="#3f6a4c"
                transform="rotate(-10 461 158)"
              />
              <ellipse
                cx="438"
                cy="178"
                rx="18"
                ry="3.4"
                fill="#6b4626"
                opacity="0.35"
              />
              <circle cx="438" cy="162" r="16" fill={url("ball")} />
              <path
                d="M425 158 Q438 150 451 158"
                stroke="#fff"
                strokeOpacity="0.35"
                strokeWidth="0.8"
                fill="none"
              />
            </g>

            {/* Rolled mats on a low bench (wider cards) */}
            <g>
              <rect
                x="30"
                y="150"
                width="90"
                height="6"
                rx="2"
                fill="#a8784a"
              />
              <rect x="34" y="156" width="4" height="12" fill="#7a5230" />
              <rect x="112" y="156" width="4" height="12" fill="#7a5230" />
              {["#8fb39a", "#e9a9a0", "#c9b8e0"].map((c, i) => (
                <g key={c}>
                  <rect
                    x={36 + i * 26}
                    y="134"
                    width="24"
                    height="16"
                    rx="8"
                    fill={c}
                  />
                  <circle
                    cx={48 + i * 26}
                    cy="142"
                    r="4"
                    fill="none"
                    stroke="#fff"
                    strokeOpacity="0.5"
                    strokeWidth="1"
                  />
                </g>
              ))}
            </g>
            <g>
              <line
                x1="560"
                y1="0"
                x2="560"
                y2="30"
                stroke="#3a3430"
                strokeWidth="1"
              />
              <circle cx="560" cy="38" r="8.4" fill="#fff8e6" />
              <path
                d="M530 166 L590 166 L586 140 L534 140 Z"
                fill={url("pot")}
              />
              <circle cx="560" cy="122" r="22" fill="#3f7a3a" />
              <circle cx="568" cy="114" r="12" fill="#5a9a4a" />
            </g>
          </svg>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_125%_at_50%_45%,transparent_58%,rgba(90,58,30,0.3)_100%)]" />
      }
    />
  );
}
